/**
 * A single listing's detail page (`/listings/:id`) — what a listing card on
 * the homepage now links to. Same actor-less, plain useState/useEffect +
 * api() pattern as PublicHomepage; still inside PublicLayout, so it shares
 * the same header/footer chrome.
 *
 * Design continues the homepage's teal-gradient-over-photo language from a
 * different anchor: the homepage's hero pins a headline top-left over the
 * photo; this page's hero pins an "info shelf" — price, specs, address — to
 * the BOTTOM of the photo, since the job here isn't a marketing headline,
 * it's presenting this one unit. Same material, deliberately different
 * composition so the two hero treatments read as siblings, not a repeat.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { CompanySettingsDto, PublicListingDto } from 'shared';
import { CoverImage } from '../../components/CoverImage.js';
import { api } from '../../lib/api.js';
import { formatPrice } from './ListingCard.js';
import { ListingsMap } from './ListingsMap.js';

export function PublicListingDetail() {
  const { id } = useParams<{ id: string }>();
  const [listing, setListing] = useState<PublicListingDto | null>(null);
  const [contact, setContact] = useState<CompanySettingsDto | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setListing(null);
    setNotFound(false);

    void api<PublicListingDto>(`/listings/${id}`)
      .then((data) => {
        if (!cancelled) setListing(data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });

    void api<CompanySettingsDto>('/contact')
      .then((data) => {
        if (!cancelled) setContact(data);
      })
      .catch(() => {
        // No contact card if this fails — never blocks the listing itself.
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (notFound) {
    return (
      <section className="public__listing-detail public__listing-detail--empty">
        <p>This listing isn't available anymore — it may have been rented or taken down.</p>
        <Link to="/" className="public__back-link">
          ← Back to all listings
        </Link>
      </section>
    );
  }

  if (!listing) {
    return (
      <section className="public__listing-detail public__listing-detail--empty">
        <p className="muted">Loading listing…</p>
      </section>
    );
  }

  const specs = [`${listing.bedrooms} bed`, `${listing.bathrooms} bath`].filter(Boolean);
  const addressLine = [listing.addressLine1, listing.city].filter(Boolean).join(', ');

  return (
    <article className="public__listing-detail">
      <section className="public__listing-hero">
        <CoverImage
          src={listing.photoUrl}
          alt={listing.addressLine1 || 'Rental listing'}
          variant="hero"
        />
        <div className="public__listing-shelf">
          <div>
            <p className="public__listing-price">${formatPrice(listing.price)}/month</p>
            {addressLine ? <p className="public__listing-address">{addressLine}</p> : null}
          </div>
          {specs.length > 0 ? (
            <ul className="public__listing-chips">
              {specs.map((spec) => (
                <li key={spec}>{spec}</li>
              ))}
            </ul>
          ) : null}
        </div>
      </section>

      <div className="public__listing-body">
        <Link to="/" className="public__back-link">
          ← Back to all listings
        </Link>

        <div className="public__listing-columns">
          <div className="public__listing-main">
            <p className="public__eyebrow">About this home</p>
            {listing.description ? (
              <p className="public__listing-description">{listing.description}</p>
            ) : (
              <p className="muted">No description has been added for this listing yet.</p>
            )}
          </div>

          <aside className="public__listing-actions">
            <div className="public__listing-action-card">
              <h2>Ready to apply?</h2>
              <p className="muted">Start your application in just a few minutes.</p>
              <Link
                to={`/apply/sign-up?unit=${listing.unitId}`}
                className="button public__apply-button"
              >
                Apply now
              </Link>
            </div>

            {contact?.contactEmail ? (
              <div className="public__listing-action-card">
                <h2>Questions about this listing?</h2>
                <p className="muted">Reach out to our leasing team directly.</p>
                <a href={`mailto:${contact.contactEmail}`} className="button button--quiet">
                  Email us
                </a>
              </div>
            ) : null}
          </aside>
        </div>

        {listing.latitude && listing.longitude ? <ListingsMap listings={[listing]} /> : null}
      </div>
    </article>
  );
}
